import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Menu, X, Plus, Minus } from 'lucide-react';
import { Link } from 'react-router-dom';
const topics = [{
  id: 'tm44',
  title: 'What is TM44 compliance?'
}, {
  id: 'fgas',
  title: 'F-Gas regulations explained'
}, {
  id: 'iso14001',
  title: 'ISO 14001 and AC compliance'
}, {
  id: 'accreditations',
  title: 'Commercial HVAC accreditations'
}];
export function Compliance() {
  const [activeId, setActiveId] = useState<string>('');
  const [isMobileTocOpen, setIsMobileTocOpen] = useState(false);
  const [openFaqId, setOpenFaqId] = useState<string | null>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id);
        }
      });
    }, {
      rootMargin: '-20% 0px -80% 0px'
    });
    topics.forEach(({
      id
    }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, []);
  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 120;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileTocOpen(false);
  };
  return <div className="min-h-screen bg-white font-sans text-primary-dark" data-id="element-1269">
      {/* Header */}
      <section className="bg-[#E8F0F8] pt-32 pb-20" data-id="element-1270">
        <div className="container mx-auto px-6" data-id="element-1271">
          <div className="max-w-4xl" data-id="element-1272">
            <div className="flex items-center gap-2 text-sm font-medium text-primary-dark/60 mb-6" data-id="element-1273">
              <Link to="/knowledge-hub" className="hover:text-accent transition-colors" data-id="element-1274">
                Resources
              </Link>
              <ChevronRight className="w-4 h-4" data-id="element-1275" />
              <span className="text-primary-dark" data-id="element-1276">Compliance</span>
            </div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-primary-dark mb-6" data-id="element-1277">
              Commercial HVAC compliance guide
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-lg text-primary-dark/70 leading-relaxed" data-id="element-1278">
              Navigate the complexities of TM44, F-Gas, and ISO 14001 with our
              comprehensive guide for Coventry and UK businesses. Published
              April 2026.
            </motion.p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-6 py-16" data-id="element-1279">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-1280">
          {/* Mobile TOC Toggle */}
          <div className="lg:hidden" data-id="element-1281">
            <button onClick={() => setIsMobileTocOpen(!isMobileTocOpen)} className="flex items-center gap-2 text-primary-dark font-medium w-full justify-between bg-[#E8F0F8] p-4 rounded-xl" data-id="element-1282">
              <span data-id="element-1283">Table of contents</span>
              {isMobileTocOpen ? <X className="w-5 h-5" data-id="element-1284" /> : <Menu className="w-5 h-5" data-id="element-1285" />}
            </button>
            {isMobileTocOpen && <div className="mt-2 bg-white border border-primary-dark/10 rounded-xl p-4 shadow-lg" data-id="element-1286">
                <nav className="flex flex-col gap-3" data-id="element-1287">
                  {topics.map(topic => <button key={topic.id} onClick={() => scrollTo(topic.id)} className={`text-left text-sm font-medium transition-colors ${activeId === topic.id ? 'text-accent' : 'text-primary-dark/70 hover:text-primary-dark'}`} data-id="element-1288">
                      {topic.title}
                    </button>)}
                </nav>
              </div>}
          </div>

          {/* Desktop TOC Sidebar */}
          <aside className="hidden lg:block w-64 shrink-0" data-id="element-1289">
            <div className="sticky top-32" data-id="element-1290">
              <h3 className="text-sm font-bold uppercase tracking-wider text-primary-dark/50 mb-6" data-id="element-1291">
                Contents
              </h3>
              <nav className="flex flex-col gap-4 border-l-2 border-primary-dark/10 pl-4" data-id="element-1292">
                {topics.map(topic => <button key={topic.id} onClick={() => scrollTo(topic.id)} className={`text-left text-sm font-medium transition-colors relative ${activeId === topic.id ? 'text-accent' : 'text-primary-dark/60 hover:text-primary-dark'}`} data-id="element-1293">
                    {activeId === topic.id && <motion.div layoutId="activeToc" className="absolute -left-[18px] top-1/2 -translate-y-1/2 w-0.5 h-full bg-accent" data-id="element-1294" />}
                    {topic.title}
                  </button>)}
              </nav>
            </div>
          </aside>

          {/* Content Area */}
          <main className="flex-1 max-w-3xl prose prose-lg prose-headings:font-serif prose-headings:text-primary-dark prose-p:text-primary-dark/80 prose-a:text-accent prose-li:text-primary-dark/80" data-id="element-1295">
            {/* Topic 1: TM44 */}
            <section id="tm44" className="mb-20 scroll-mt-32" data-id="element-1296">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1297">
                What is TM44 compliance?
              </h2>
              <p data-id="element-1298">
                TM44 compliance mandates UK businesses with over 12kW of
                combined air conditioning capacity (e.g., retail stores,
                offices) to inspect systems every 5 years via a registered
                energy assessor. It checks efficiency, sizing, and maintenance
                to cut energy waste.
              </p>
              <p data-id="element-1299">
                Failure risks £5,000 fines during ISO 14001 audits or local
                authority checks. AccuTemp provides white-labelled TM44 reports
                nationwide from our Coventry base.
              </p>

              <h3 className="text-2xl font-serif mt-8 mb-4" data-id="element-1300">Who needs TM44?</h3>
              <ul className="list-disc pl-6 mb-6 space-y-2" data-id="element-1301">
                <li data-id="element-1302">
                  Buildings with over 12kW total cooling capacity (check outdoor
                  unit labels).
                </li>
                <li data-id="element-1303">
                  Must be renewed every 5 years from the installation date.
                </li>
                <li data-id="element-1304">Often triggered by ISO audits or property sales.</li>
              </ul>

              <h3 className="text-2xl font-serif mt-8 mb-4" data-id="element-1305">
                The inspection process
              </h3>
              <ol className="list-decimal pl-6 mb-8 space-y-2" data-id="element-1306">
                <li data-id="element-1307">Assessor logs all assets and creates a register.</li>
                <li data-id="element-1308">Checks maintenance logs, system sizing, and controls.</li>
                <li data-id="element-1309">
                  Issues a detailed report with recommendations (e.g., adjusting
                  timers).
                </li>
              </ol>

              <div className="bg-[#E8F0F8] p-8 rounded-2xl my-8" data-id="element-1310">
                <h4 className="text-xl font-serif mb-4" data-id="element-1311">
                  Need a TM44 inspection?
                </h4>
                <p className="mb-6 text-base" data-id="element-1312">
                  Ensure your Coventry business is fully compliant and avoid
                  unexpected fines.
                </p>
                <Link to="/#contact" className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-white px-6 py-3 rounded-full text-sm font-medium transition-colors" data-id="element-1313">
                  Get TM44 quote Coventry
                  <ChevronRight className="w-4 h-4" data-id="element-1314" />
                </Link>
              </div>

              {/* FAQ Schema Block */}
              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-1315">
                <h3 className="text-2xl font-serif mb-6" data-id="element-1316">TM44 FAQs</h3>
                <div className="space-y-4" data-id="element-1317">
                  {[{
                  q: 'Does a small office need a TM44?',
                  a: 'No, if your total combined cooling capacity is under 12kW, you are exempt.'
                }, {
                  q: 'How much does a TM44 inspection cost?',
                  a: 'Costs typically range from £200 to £500 per site depending on the number of units and complexity. Contact us for a precise quote.'
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-1318">
                      <button onClick={() => setOpenFaqId(openFaqId === `tm44-${i}` ? null : `tm44-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-1319">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-1320">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-1321">
                          {openFaqId === `tm44-${i}` ? <Minus className="w-5 h-5" data-id="element-1322" /> : <Plus className="w-5 h-5" data-id="element-1323" />}
                        </span>
                      </button>
                      {openFaqId === `tm44-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-1324">
                          <p itemProp="text" data-id="element-1325">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>

            {/* Topic 2: F-Gas */}
            <section id="fgas" className="mb-20 scroll-mt-32" data-id="element-1326">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1327">
                F-Gas regulations explained
              </h2>
              <p data-id="element-1328">
                F-Gas regulations aim to phase out high Global Warming Potential
                (GWP) refrigerants and prevent harmful leaks into the
                atmosphere. Regular leak checks are a legal requirement based on
                the CO2 equivalent charge of your system.
              </p>
              <p data-id="element-1329">
                AccuTemp is fully REFCOM certified, ensuring all our engineers
                are legally qualified to handle refrigerants and maintain your
                F-Gas logs.
              </p>

              <h3 className="text-2xl font-serif mt-8 mb-4" data-id="element-1330">
                Leak check frequencies
              </h3>
              <div className="overflow-x-auto mb-8" data-id="element-1331">
                <table className="w-full text-left border-collapse" data-id="element-1332">
                  <thead data-id="element-1333">
                    <tr className="bg-[#E8F0F8]" data-id="element-1334">
                      <th className="p-4 font-medium text-primary-dark border-b border-primary-dark/10" data-id="element-1335">
                        CO2 Equivalent
                      </th>
                      <th className="p-4 font-medium text-primary-dark border-b border-primary-dark/10" data-id="element-1336">
                        Check Frequency
                      </th>
                      <th className="p-4 font-medium text-primary-dark border-b border-primary-dark/10" data-id="element-1337">
                        With Leak Detection System
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-1338">
                    <tr data-id="element-1339">
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1340">
                        5 to 50 tonnes
                      </td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1341">
                        Every 12 months
                      </td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1342">
                        Every 24 months
                      </td>
                    </tr>
                    <tr data-id="element-1343">
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1344">
                        50 to 500 tonnes
                      </td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1345">
                        Every 6 months
                      </td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1346">
                        Every 12 months
                      </td>
                    </tr>
                    <tr data-id="element-1347">
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1348">
                        Over 500 tonnes
                      </td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1349">
                        Every 3 months
                      </td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1350">
                        Every 6 months
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p data-id="element-1351">
                For example, an R410A system with more than 2.4kg of refrigerant
                requires a check every 12 months.{' '}
                <Link to="/knowledge-hub/maintenance-guide" data-id="element-1352">
                  Regular maintenance contracts
                </Link>{' '}
                automatically cover these checks.
              </p>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-1353">
                <h3 className="text-2xl font-serif mb-6" data-id="element-1354">F-Gas FAQs</h3>
                <div className="space-y-4" data-id="element-1355">
                  {[{
                  q: 'What are the fines for F-Gas non-compliance?',
                  a: 'Fines are potentially unlimited and can be issued by the Environment Agency for failing to prevent leaks or maintain proper records.'
                }, {
                  q: 'Do I need both TM44 and F-Gas checks?',
                  a: 'Yes, they are separate legal requirements. TM44 is for energy efficiency (every 5 years), while F-Gas is for environmental leak prevention (every 3-12 months).'
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-1356">
                      <button onClick={() => setOpenFaqId(openFaqId === `fgas-${i}` ? null : `fgas-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-1357">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-1358">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-1359">
                          {openFaqId === `fgas-${i}` ? <Minus className="w-5 h-5" data-id="element-1360" /> : <Plus className="w-5 h-5" data-id="element-1361" />}
                        </span>
                      </button>
                      {openFaqId === `fgas-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-1362">
                          <p itemProp="text" data-id="element-1363">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>

            {/* Topic 3: ISO 14001 */}
            <section id="iso14001" className="mb-20 scroll-mt-32" data-id="element-1364">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1365">
                ISO 14001 and AC compliance
              </h2>
              <p data-id="element-1366">
                ISO 14001 is the international standard for environmental
                management systems. If your business holds or is working towards
                ISO 14001, your HVAC systems will be heavily scrutinised during
                audits.
              </p>
              <p data-id="element-1367">
                Auditors will specifically look for valid TM44 certificates,
                up-to-date F-Gas logs, and evidence of proactive maintenance to
                reduce energy consumption. AccuTemp prepares your documentation
                to ensure you pass without non-conformances.
              </p>

              <h3 className="text-2xl font-serif mt-8 mb-4" data-id="element-1368">
                How to prepare for an audit
              </h3>
              <ul className="list-disc pl-6 mb-8 space-y-2" data-id="element-1369">
                <li data-id="element-1370">Ensure all F-Gas logs are digitised and accessible.</li>
                <li data-id="element-1371">
                  Have your latest TM44 report ready and demonstrate action
                  taken on its recommendations.
                </li>
                <li data-id="element-1372">
                  Show a scheduled maintenance plan with a certified contractor.
                </li>
              </ul>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-1373">
                <h3 className="text-2xl font-serif mb-6" data-id="element-1374">ISO 14001 FAQs</h3>
                <div className="space-y-4" data-id="element-1375">
                  {[{
                  q: 'Will I fail my ISO audit without a TM44?',
                  a: 'Yes, missing a legally required TM44 certificate is a major non-conformance that can result in your ISO 14001 certification being denied or suspended.'
                }, {
                  q: 'Can AccuTemp help with audit documentation?',
                  a: 'Absolutely. We provide comprehensive, audit-ready service reports and compliance documentation for all our contracted clients.'
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-1376">
                      <button onClick={() => setOpenFaqId(openFaqId === `iso-${i}` ? null : `iso-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-1377">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-1378">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-1379">
                          {openFaqId === `iso-${i}` ? <Minus className="w-5 h-5" data-id="element-1380" /> : <Plus className="w-5 h-5" data-id="element-1381" />}
                        </span>
                      </button>
                      {openFaqId === `iso-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-1382">
                          <p itemProp="text" data-id="element-1383">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>

            {/* Topic 4: Accreditations */}
            <section id="accreditations" className="mb-20 scroll-mt-32" data-id="element-1384">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1385">
                Commercial HVAC accreditations
              </h2>
              <p data-id="element-1386">
                When selecting a commercial HVAC partner, accreditations are
                your guarantee of safety, quality, and financial stability. For
                public sector tenders and large commercial projects, these are
                non-negotiable.
              </p>

              <h3 className="text-2xl font-serif mt-8 mb-4" data-id="element-1387">
                Key accreditations to look for
              </h3>
              <ul className="list-disc pl-6 mb-8 space-y-2" data-id="element-1388">
                <li data-id="element-1389">
                  <strong data-id="element-1390">Constructionline Gold:</strong> The hardest tier to
                  achieve, requiring rigorous checks on environmental
                  management, quality management, and equal opportunities. Only
                  1 in 100 HVAC firms hold this.
                </li>
                <li data-id="element-1391">
                  <strong data-id="element-1392">
                    CHAS (Contractors Health and Safety Assessment Scheme):
                  </strong>{' '}
                  Demonstrates compliance with health and safety law.
                </li>
                <li data-id="element-1393">
                  <strong data-id="element-1394">REFCOM:</strong> Legally required for handling
                  fluorinated greenhouse gases.
                </li>
                <li data-id="element-1395">
                  <strong data-id="element-1396">Manufacturer Partnerships:</strong> E.g., Daikin D1+
                  Premier Partner status, which guarantees engineers are
                  factory-trained and unlocks extended warranties.
                </li>
              </ul>

              <p data-id="element-1397">
                AccuTemp holds all of the above, making us a safe, compliant
                choice for M&E contractors, FM companies, and direct clients
                across the West Midlands.
              </p>

              <div className="bg-[#E8F0F8] p-8 rounded-2xl my-8" data-id="element-1398">
                <h4 className="text-xl font-serif mb-4" data-id="element-1399">
                  Need a compliant partner for your next tender?
                </h4>
                <p className="mb-6 text-base" data-id="element-1400">
                  Partner with a Constructionline Gold accredited specialist.
                </p>
                <Link to="/#contact" className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-white px-6 py-3 rounded-full text-sm font-medium transition-colors" data-id="element-1401">
                  Contact our commercial team
                  <ChevronRight className="w-4 h-4" data-id="element-1402" />
                </Link>
              </div>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-1403">
                <h3 className="text-2xl font-serif mb-6" data-id="element-1404">Accreditation FAQs</h3>
                <div className="space-y-4" data-id="element-1405">
                  {[{
                  q: 'Are these accreditations essential for public sector tenders?',
                  a: 'Yes, PAS 91 and Constructionline Gold are often mandatory pre-qualification requirements for local authority and government contracts.'
                }, {
                  q: 'What does Daikin D1+ status mean for me?',
                  a: 'It means we can offer extended warranties of up to 16 years, priority technical support, and guaranteed high-quality installations.'
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-1406">
                      <button onClick={() => setOpenFaqId(openFaqId === `acc-${i}` ? null : `acc-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-1407">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-1408">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-1409">
                          {openFaqId === `acc-${i}` ? <Minus className="w-5 h-5" data-id="element-1410" /> : <Plus className="w-5 h-5" data-id="element-1411" />}
                        </span>
                      </button>
                      {openFaqId === `acc-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-1412">
                          <p itemProp="text" data-id="element-1413">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>;
}